import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Home, Wrench, Bike, Ruler, Mail, Menu, X, PartyPopper } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const NAV_ITEMS = [
  { to: "/", label: "Home", icon: Home, testid: "nav-home-link" },
  { to: "/services", label: "Services", icon: Bike, testid: "nav-services-link" },
  { to: "/repairs", label: "Repairs", icon: Wrench, testid: "nav-repairs-link" },
  { to: "/fitting", label: "Fitting", icon: Ruler, testid: "nav-fitting-link" },
  { to: "/events", label: "Events", icon: PartyPopper, testid: "nav-events-link" },
  { to: "/contact", label: "Contact", icon: Mail, testid: "nav-contact-link" },
];

function Logo() {
  return (
    <Link to="/" data-testid="site-logo" className="group flex items-center gap-2.5">
      <img
        src="/logo-nav.png"
        alt="Last Stop Bike Shop crest"
        className="h-11 w-auto transition-transform group-hover:-rotate-3"
      />
      <span className="flex flex-col leading-none">
        <span className="font-display text-lg font-bold tracking-tight text-leaf-900">
          Last Stop
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-leaf-700">
          Bike Shop
        </span>
      </span>
    </Link>
  );
}

function DesktopNav() {
  return (
    <header className="sticky top-4 z-40 mx-auto hidden w-[min(100%-2rem,1200px)] md:block">
      <nav
        data-testid="desktop-nav"
        className="flex items-center justify-between rounded-full border border-leaf-200/70 bg-white/85 px-5 py-3 shadow-[0_8px_30px_rgba(21,128,61,0.08)] backdrop-blur-md"
      >
        <Logo />
        <ul className="flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                data-testid={item.testid}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                    isActive
                      ? "bg-leaf-600 text-white shadow"
                      : "text-leaf-900 hover:bg-leaf-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <Link
          to="/contact#booking"
          data-testid="cta-book-top"
          className="rounded-full bg-tomato-500 px-5 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-tomato-600 hover:shadow-lg"
        >
          Request Service
        </Link>
      </nav>
    </header>
  );
}

function MobileTopBar({ onMenu }) {
  return (
    <header className="sticky top-0 z-40 block border-b border-leaf-200/50 bg-background/90 backdrop-blur-md md:hidden">
      <div className="flex items-center justify-between px-4 py-3">
        <Logo />
        <button
          data-testid="mobile-menu-button"
          onClick={onMenu}
          aria-label="Open menu"
          className="rounded-full bg-leaf-100 p-2.5 text-leaf-900 active:scale-95"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </header>
  );
}

function MobileBottomNav() {
  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-leaf-200 bg-white/95 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 shadow-[0_-10px_30px_rgba(0,0,0,0.08)] backdrop-blur-md md:hidden"
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around px-2">
        {NAV_ITEMS.map((item) => (
          <li key={item.to} className="flex-1">
            <NavLink
              to={item.to}
              data-testid={`bottom-${item.testid}`}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 rounded-xl px-1 py-1.5 text-[11px] font-semibold transition-colors ${
                  isActive
                    ? "text-leaf-700"
                    : "text-leaf-900/60 hover:text-leaf-800"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full transition-all ${
                      isActive ? "bg-leaf-600 text-white" : "text-current"
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                  </span>
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function MobileMenuDrawer({ open, onClose }) {
  const { settings } = useSettings();
  if (!open) return null;
  return (
    <div
      data-testid="mobile-menu-drawer"
      className="fixed inset-0 z-50 md:hidden"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-leaf-900/30 backdrop-blur-sm" />
      <div
        className="absolute right-0 top-0 h-full w-[86%] max-w-sm overflow-y-auto bg-cream-50 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <Logo />
          <button
            data-testid="mobile-menu-close"
            aria-label="Close menu"
            onClick={onClose}
            className="rounded-full bg-leaf-100 p-2 active:scale-95"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <ul className="mt-8 space-y-2">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onClose}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-2xl px-4 py-3.5 text-base font-semibold transition-colors ${
                    isActive
                      ? "bg-leaf-600 text-white"
                      : "bg-white text-leaf-900 hover:bg-leaf-100"
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <Link
          to="/contact#booking"
          onClick={onClose}
          data-testid="mobile-drawer-book"
          className="mt-8 block rounded-full bg-tomato-500 px-5 py-3.5 text-center text-base font-bold text-white hover:bg-tomato-600"
        >
          Request Service
        </Link>
        <div className="mt-8 text-sm text-leaf-800/70">
          <p className="font-semibold">Last Stop Bike Shop</p>
          <p>{settings.short_address}</p>
          <a href={`mailto:${settings.email}`} className="underline">
            {settings.email}
          </a>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  const { settings } = useSettings();
  return (
    <footer
      data-testid="site-footer"
      className="relative mt-16 overflow-hidden bg-leaf-900 px-4 pb-28 pt-16 text-leaf-50 md:pb-16 md:px-12"
    >
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-leaf-700/40 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 top-10 h-72 w-72 rounded-full bg-sun-500/20 blur-3xl" />
      <div className="relative mx-auto grid max-w-6xl gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-sun-50 p-1.5 ring-2 ring-sun-300/40">
              <img src="/logo-nav.png" alt="Last Stop Bike Shop crest" className="h-full w-auto" />
            </span>
            <span className="font-display text-2xl font-bold">Last Stop</span>
          </div>
          <p className="mt-4 max-w-md text-leaf-100/80">
            A community-first bike shop in St. Louis rooted in no-ego service,
            custom builds, and teaching the craft of the bike.
          </p>
          <p className="mt-6 text-sm text-leaf-100/60">
            Built for the people. Made to last.
          </p>
        </div>
        <div>
          <h4 className="font-display text-lg font-semibold text-sun-200">
            Visit
          </h4>
          <p className="mt-3 text-sm text-leaf-100/80">
            {settings.address}
          </p>
          <a
            href={`tel:${(settings.phone || "").replace(/\D/g, "")}`}
            className="mt-2 block text-sm text-leaf-100/80 hover:text-white"
          >
            {settings.phone}
          </a>
          <a
            href={`mailto:${settings.email}`}
            className="block text-sm text-leaf-100/80 hover:text-white"
          >
            {settings.email}
          </a>
        </div>
        <div>
          <h4 className="font-display text-lg font-semibold text-sun-200">
            Explore
          </h4>
          <ul className="mt-3 space-y-2 text-sm">
            {NAV_ITEMS.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  className="text-leaf-100/80 hover:text-white"
                  data-testid={`footer-${n.testid}`}
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="relative mx-auto mt-10 max-w-6xl border-t border-leaf-700/60 pt-6 text-xs text-leaf-100/50">
        © {new Date().getFullYear()} Last Stop Bike Shop. Keep the wheels turning.
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close any open menu on route change
  React.useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <MobileTopBar onMenu={() => setMenuOpen(true)} />
      <DesktopNav />
      <MobileMenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
      <main data-testid="main-content" className="pb-24 md:pb-0">
        {children}
      </main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
}
